import React, { useEffect, useMemo, useState } from 'react';
import List from '@material-ui/core/List';
import HomeIcon from '@material-ui/icons/Home';
import AppsIcon from '@material-ui/icons/Apps';
import GroupIcon from '@material-ui/icons/Group';
import SettingsIcon from '@material-ui/icons/Settings';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import ListItem from '@material-ui/core/ListItem';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import Collapse from '@material-ui/core/Collapse';
import Badge from '@material-ui/core/Badge';
import { useOrganizationStatus } from '../../queries/useOrganizationStatus';

interface SidebarMenuItemProps {
    to: string;
    text: string;
    icon?: React.ReactNode;
    cb: () => void;
    expandable?: boolean;
    children?: React.ReactNode;
    badgeCount?: number;
}

const SidebarMenuItem = ({
    to,
    text,
    icon,
    cb,
    expandable,
    children,
    badgeCount = 0,
}: SidebarMenuItemProps): JSX.Element => {
    const location = useLocation();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const ExpandToggle = () =>
        expandable ? <>{isExpanded ? <ExpandLess /> : <ExpandMore />}</> : <React.Fragment />;

    return (
        <>
            <ListItem
                button
                component={RouterLink}
                to={to}
                selected={location.pathname === to}
                onClick={() => {
                    if (expandable) {
                        setIsExpanded(!isExpanded);
                    }

                    cb();
                }}
            >
                {icon && <ListItemIcon>{icon}</ListItemIcon>}
                <ListItemText>{text}</ListItemText>
                {expandable && badgeCount > 0 ? (
                    <Badge badgeContent={badgeCount} color={'error'}>
                        <ExpandToggle />
                    </Badge>
                ) : (
                    <ExpandToggle />
                )}
            </ListItem>
            {expandable && children && (
                <Collapse in={isExpanded} timeout={'auto'} unmountOnExit>
                    {children}
                </Collapse>
            )}
        </>
    );
};

interface SidebarMenuProps {
    callback: () => void;
}

export const SidebarMenu = ({ callback }: SidebarMenuProps): JSX.Element => {
    const { hasValidPayoutSettings, hasValidTaxForm, hasUnclaimedPayout, loading, refetch } =
        useOrganizationStatus();

    const badgeCount = useMemo(() => {
        let count = 0;

        if (!loading) {
            if (!hasValidPayoutSettings) {
                count = count + 1;
            }
            if (!hasValidTaxForm) {
                count = count + 1;
            }
            if (hasUnclaimedPayout) {
                count = count + 1;
            }
        }

        return count;
    }, [loading, hasValidTaxForm, hasValidPayoutSettings, hasUnclaimedPayout]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    return (
        <List>
            <SidebarMenuItem to="/extranet" text="Home" icon={<HomeIcon />} cb={callback} />
            <SidebarMenuItem to="/extranet/apps" text="Apps" icon={<AppsIcon />} cb={callback} />
            <SidebarMenuItem
                to="/extranet/payouts"
                text="Payouts"
                icon={<MonetizationOnIcon />}
                expandable={true}
                badgeCount={badgeCount ? badgeCount : undefined}
                cb={callback}
            >
                <SidebarMenuItem
                    to="/extranet/payouts/settings"
                    text="Payout settings"
                    cb={callback}
                />
                <SidebarMenuItem to="/extranet/payouts/tax-form" text="Tax form" cb={callback} />
            </SidebarMenuItem>
            <SidebarMenuItem to="/extranet/team" text="Team" icon={<GroupIcon />} cb={callback} />
            <SidebarMenuItem
                to="/extranet/account"
                text="Account"
                icon={<SettingsIcon />}
                cb={callback}
            />
        </List>
    );
};
